import { motion } from "framer-motion";
import Logo from "@/components/ui/Logo";
import NovariMark from "@/components/ui/NovariMark";
import TypewriterFeatures from "./components/TypewriterFeatures";
import { StudyIllustration } from "@/components/illustrations/StudyIllustration";
import "./AuthShell.css";

interface AuthShellProps {
  children: React.ReactNode;
  kids?: boolean;
  signIn?: boolean;
  /** Which story the card's illustrated panel tells. Defaults to sign-in's. */
  story?: "signIn" | "signUp";
}

const STORIES = {
  signIn: {
    label: "Sign in to Novari",
    eyebrow: "YOUR NEXT CHAPTER",
    title: <>A little progress.<br />A clearer tomorrow.</>,
    body: "Your notes, your questions, your next breakthrough. Make room for a little learning, at your own pace.",
    page: "01 — A fresh start",
  },
  signUp: {
    label: "Create your Novari account",
    eyebrow: "YOUR FIRST CHAPTER",
    title: <>Start where you are.<br />Walk in ready.</>,
    body: "Bring your notes and past questions. Novari turns them into practice and a plan you can follow, one session at a time.",
    page: "00 — Day one",
  },
} as const;

export default function AuthShell({ children, kids = false, signIn = false, story }: AuthShellProps) {
  if (!kids) {
    const tale = STORIES[story ?? "signIn"];
    return (
      <main className="sign-in-page">
        <div className={story === "signUp" ? "sign-in-card sign-in-card--long" : "sign-in-card"}>
          <section className="sign-in-form-panel" aria-label={tale.label}>
            <div className="sign-in-logo"><Logo showText clickable={false} size="md" /><a className="sign-in-back" href="/">← Back to home</a></div>
            <div className="sign-in-form">{children}</div>
            <p className="sign-in-footnote">A little learning, every day.</p>
          </section>
          <aside className="sign-in-story" aria-label="Your study space">
            <div className="sign-in-story-inner">
              <div className="sign-in-story-copy">
                <span className="sign-in-eyebrow">{tale.eyebrow}</span>
                <h2 className="font-serif">{tale.title}</h2>
                <p>{tale.body}</p>
                <div className="sign-in-story-signature"><NovariMark size={24} color="#fff" /><span>A calmer way to get ready.</span></div>
              </div>
              <StudyIllustration className="sign-in-books" />
              <span className="sign-in-page-number" aria-hidden="true">{tale.page}</span>
            </div>
          </aside>
        </div>
      </main>
    );
  }
  return (
    <div
      className="auth-shell min-h-screen w-full flex relative overflow-hidden"
      style={{ background: "var(--bg-primary)" }}
    >
      {/* ── Left Brand Panel (desktop only) ── */}
      <div
        className="hidden lg:flex lg:w-[44%] flex-col justify-between relative overflow-hidden flex-shrink-0 z-10 p-12"
        style={{
          background: "var(--bg-surface)",
          borderRight: "1px solid var(--border-default)",
        }}
      >
        <span className="absolute inset-x-0 top-0 h-px bg-[var(--border-default)]" aria-hidden="true" />
        {/* Panel content */}
        <div className="relative z-10 flex flex-col h-full gap-8 justify-start">
          {/* Logo */}
          <div className="flex items-start justify-between">
            <NovariMark size={76} color="var(--accent-primary)" />
            <a className="sign-in-back" href="/">← Back to home</a>
          </div>

          {/* Headline + features */}
          <div className="flex flex-col gap-8 pt-[12vh]">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h2
                className="font-serif text-5xl font-semibold leading-[0.96] tracking-tight mb-5"
                style={{ color: "var(--text-primary)" }}
              >
                {kids ? "A little curiosity." : "A calmer way to"} <span style={{ color: "var(--accent-primary)" }}>{kids ? "A new adventure." : "get ready."}</span>
              </h2>
              <p
                className="text-base leading-relaxed"
                style={{ color: "var(--text-secondary)" }}
              >
                {kids ? "A friendly space to ask questions, practise and discover something new. A parent or grown-up can help you sign in." : "A focused study space that helps you know what to do next, then do it well."}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              {kids ? <p className="text-lg text-[var(--accent-primary)]">Ask. Explore. Learn at your own pace.</p> : <TypewriterFeatures />}
            </motion.div>
          </div>
        </div>
      </div>

      {/* ── Right Form Panel ── */}
      <div className="flex-1 flex items-center justify-center px-5 py-8 sm:p-8 lg:p-12 overflow-y-auto relative z-10">
        <div className="w-full max-w-[440px] p-0 sm:p-8 lg:p-10 relative">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center justify-between gap-2 mb-9 relative z-10">
            <Logo showText clickable={false} size="md" />
            <a className="sign-in-back" href="/">← Home</a>
          </div>

          <div className="relative z-10">{children}</div>
        </div>
      </div>
    </div>
  );
}
